import { Box, Button, Card, Chip, Stack, Typography, TextField, MenuItem, Pagination } from '@mui/material';
import { useState, useEffect } from 'react';
import { useTranslate, Title } from 'react-admin';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PeopleIcon from '@mui/icons-material/People';
import NoteIcon from '@mui/icons-material/Note';
import FilterListIcon from '@mui/icons-material/FilterList';

import { API_HANDLERS } from '@/api';
import { PushCommunication, PushCommunicationFilters } from '@/types';
import { SendPushModal } from './components/SendPushModal';

const getStatusColor = (status: string): string => {
  switch (status) {
    case 'completed':
      return '#4caf50';
    case 'inprogress':
      return '#2196f3';
    case 'pending':
      return '#ff9800';
    case 'failed':
      return '#f44336';
    default:
      return '#9e9e9e';
  }
};

const getStatusLabel = (status: string, translate: any): string => {
  return translate(`pos.communication.status.${status}`);
};

export function CommunicationList(): JSX.Element {
  const translate = useTranslate();
  const [communications, setCommunications] = useState<PushCommunication[]>([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [filters, setFilters] = useState<PushCommunicationFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const fetchCommunications = async () => {
    try {
      setLoading(true);
      const response = await API_HANDLERS.COMMUNICATION.GET_HISTORY({
        page,
        take: pageSize,
        filters,
      });
      if (response.data) {
        setCommunications(response.data.data);
        setTotalPages(response.data.meta.pageCount);
        setTotalItems(response.data.meta.itemCount);
      }
    } catch (error) {
      console.error('Error fetching communications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCommunications();
  }, [page, pageSize, filters]);

  const handleSendSuccess = () => {
    setOpenModal(false);
    setPage(1);
    fetchCommunications();
  };

  const handleFilterChange = (field: keyof PushCommunicationFilters, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value || undefined }));
    setPage(1); // Reset to first page when filters change
  };

  const handleResetFilters = () => {
    setFilters({});
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('uk-UA', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Title title={translate('pos.communication.title')} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            {translate('pos.communication.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {translate('pos.communication.subtitle')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            onClick={() => setShowFilters(!showFilters)}
            startIcon={<FilterListIcon />}
            sx={{ textTransform: 'none' }}
          >
            {translate('pos.communication.filters.toggle')}
          </Button>
          <Button
            variant="contained"
            onClick={() => setOpenModal(true)}
            sx={{
              backgroundColor: '#7c3aed',
              '&:hover': { backgroundColor: '#6d28d9' },
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1.5,
            }}
            startIcon={<span style={{ fontSize: '20px' }}>+</span>}
          >
            {translate('pos.communication.create_button')}
          </Button>
        </Stack>
      </Box>

      {showFilters && (
        <Card sx={{ p: 3, mb: 3 }}>
          <Stack spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {translate('pos.communication.filters.title')}
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <TextField
                select
                label={translate('pos.communication.filters.status')}
                value={filters.status || ''}
                onChange={e => handleFilterChange('status', e.target.value)}
                sx={{ minWidth: 200 }}
                size="small"
              >
                <MenuItem value="">
                  {translate('pos.communication.filters.all_statuses')}
                </MenuItem>
                <MenuItem value="pending">{translate('pos.communication.status.pending')}</MenuItem>
                <MenuItem value="inprogress">{translate('pos.communication.status.inprogress')}</MenuItem>
                <MenuItem value="completed">{translate('pos.communication.status.completed')}</MenuItem>
                <MenuItem value="failed">{translate('pos.communication.status.failed')}</MenuItem>
              </TextField>

              <TextField
                type="date"
                label={translate('pos.communication.filters.start_date')}
                value={filters.startDate || ''}
                onChange={e => handleFilterChange('startDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ minWidth: 200 }}
              />

              <TextField
                type="date"
                label={translate('pos.communication.filters.end_date')}
                value={filters.endDate || ''}
                onChange={e => handleFilterChange('endDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ minWidth: 200 }}
              />

              <Button variant="outlined" onClick={handleResetFilters} sx={{ textTransform: 'none' }}>
                {translate('pos.communication.filters.reset')}
              </Button>
            </Stack>
          </Stack>
        </Card>
      )}

      {loading ? (
        <Typography>{translate('ra.page.loading')}</Typography>
      ) : (
        <Stack spacing={2}>
          {communications.map(comm => (
            <Card key={comm.id} sx={{ p: 3, boxShadow: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {comm.push_title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    {comm.push_text}
                  </Typography>
                  <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <AccessTimeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(comm.createdAt)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PeopleIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {translate('pos.communication.recipients_count', {
                          count: comm.sent_count,
                          total: comm.total_count,
                        })}
                      </Typography>
                    </Box>
                    {comm.note && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <NoteIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {comm.note}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Box>
                <Chip
                  label={getStatusLabel(comm.status, translate)}
                  sx={{
                    backgroundColor: getStatusColor(comm.status),
                    color: 'white',
                    fontWeight: 600,
                    ml: 2,
                  }}
                />
              </Box>
            </Card>
          ))}
        </Stack>
      )}

      {!loading && communications.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {translate('pos.communication.pagination.showing', {
              from: (page - 1) * pageSize + 1,
              to: Math.min(page * pageSize, totalItems),
              total: totalItems,
            })}
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              select
              size="small"
              value={pageSize}
              onChange={e => handlePageSizeChange(Number(e.target.value))}
              sx={{
                minWidth: 100,
                '& .MuiInputBase-input': {
                  paddingTop: '8.5px',
                  paddingBottom: '8.5px',
                  lineHeight: 1,
                },
              }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </TextField>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, newPage) => handlePageChange(newPage)}
              color="primary"
              shape="rounded"
            />
          </Stack>
        </Box>
      )}

      {communications.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            {translate('pos.communication.empty')}
          </Typography>
        </Box>
      )}

      <SendPushModal open={openModal} onClose={() => setOpenModal(false)} onSuccess={handleSendSuccess} />
    </Box>
  );
}

export default CommunicationList;
